// 메신저 그림 미리보기 — 손가락 동작(핀치 줌 · 확대 중 한 손가락 끌기 · 좌우로 밀어 다음/이전).
// 설계: `patch_mm_preview_touch_gestures.md`(마스터 2026-10-01 «핀치 줌 할 수 있도록 가능하냐?»
// → «이제보니 좌우로 움직이는것도 안되네»).
//
// 🔴 MM 11.11 미리보기는 마우스(휠·끌기)와 키보드(←/→)만 알아듣는다(번들 실측: 그 청크에
//    touchstart·pointerdown·touch-action 이 0건). 그래서 우리는 «손가락 → MM 이 이미 받는 이벤트»
//    로 바꿔 넘긴다 — MM 의 React 상태(scale/translate)나 img 의 인라인 style 을 직접 쓰지 않는다.
//    ① 핀치      → 래퍼(.image_preview)에 wheel (MM handleImageWheel, 네이티브 리스너)
//    ② 확대 중 끌기 → 래퍼에 mousedown + document 에 mousemove·mouseup (MM handleImageMouseDown)
//    ③ 좌우로 밀기  → document 에 keyup ArrowRight/ArrowLeft (MM handleKeyPress)
// 🔴 MM 휠 규칙: 이벤트 하나가 배율을 «최대 0.25 더하거나 뺀다»(곱이 아니다 — `|deltaY|/100` 을
//    1 로 깎아 SCALE_DELTA 에 곱한다). deltaY 음수 = 확대. 그래서 목표 배율까지 여러 번 쪼개 보낸다.
// 🔴 목표 배율은 «핀치 시작 때 읽은 배율 + 내부 누적값»으로만 계산한다 — move 마다 DOM 을 다시 읽으면
//    React 렌더가 한 프레임 늦는 사이 배율이 넘친다(확대 중에는 transform transition 도 걸려 있다).
// 🔴 대상 판정은 «제스처의 첫 touchstart 에서 한 번만». 미리보기 밖(헤더 버튼·확대 막대·채팅 본문)은
//    아예 손대지 않는다 — 탭이 그대로 클릭으로 가야 한다.
// 🔴 전부 try/catch — 셀렉터가 어긋나거나 MM 이 바뀌면 조용히 무동작 = 지금(터치 미지원)으로 돌아간다.
// 🔴 게이트웨이 viewport(`user-scalable=no`)·MobileScale 의 두 손가락 차단은 그대로 둔다(화면 전체
//    확대를 다시 켜지 않는다). 우리 리스너는 capture 라 그쪽과 충돌하지 않는다.
export const STYLE_ID = 'gw-mm-preview-touch';
export const MODAL_SEL = '.file-preview-modal';
export const AREA_SEL = '.file-preview-modal__content';
export const WRAP_SEL = '.image_preview';                 // 다운로드 금지 채널은 <a> 대신 <span>
export const IMG_SEL = 'img.image_preview__image';
export const ZOOM_BAR_SEL = '.file-preview-modal__zoom-bar';
// «원래 크기»(handleZoomReset) 버튼 — 지금 배율이 기본보다 크면 magnify-minus, 작으면 magnify-plus.
export const RESET_ICON_SEL = 'i.icon-magnify-minus, i.icon-magnify-plus';
export const SCALE_DELTA = 0.25;    // MM 휠 이벤트 하나가 바꾸는 배율 상한
export const WHEEL_UNIT = 100;      // MM 이 SCALE_DELTA 를 꽉 채우는 |deltaY|
export const MIN_SCALE = 0.25;      // MM MIN_SCALE
export const MAX_SCALE = 5;         // MM getMaxScaleForFile(그림)
export const MAX_WHEEL_STEPS = 24;  // 한 move 가 보낼 수 있는 wheel 개수 상한(폭주 차단)
export const STEP_EPS = 0.005;      // 남은 차이가 이보다 작으면 버린다(의미 없는 wheel 금지)
export const SNAP_EPS = 0.1;        // 핀치 끝 |배율-1| < 이 값이면 «원래 크기» 버튼을 누른다
export const DRAG_SCALE_EPS = 1.001; // 이보다 커야 MM 이 끌기를 받는다(기본 배율 초과)
export const SWIPE_MIN_PX = 60;
export const SWIPE_RATIO = 1.5;     // |dx| 가 |dy| 의 이 배 이상일 때만 «가로 밀기»
export const SWIPE_MAX_MS = 600;
export const SWIPE_PREVENT_PX = 10; // 가로로 이만큼 넘게 움직였으면 탭 클릭이 안 나가게 막는다
export const TOUCH_CSS = AREA_SEL + '{touch-action:none}';

function round6(v) { return Math.round(v * 1e6) / 1e6; }

function clampScale(v) {
  if (!Number.isFinite(v)) return 1;
  if (v < MIN_SCALE) return MIN_SCALE;
  if (v > MAX_SCALE) return MAX_SCALE;
  return v;
}

// 지금 배율 — «인라인 `img.style.transform`» 의 scale() 만 본다(getComputedStyle 은 등장 애니메이션
// `image_preview-zoom-in` 의 scale(.8) 이 섞인다). 배율 1·이동 0 이면 MM 이 transform 을 아예 안 쓴다 = 1.
// 🔴 유한한 양수가 아니면(NaN·Infinity·0·음수) 1 — 읽기 실패가 엉뚱한 목표 배율로 번지지 않게.
// 요소 대신 transform 문자열을 바로 넣어도 된다(시험·재사용 편의).
export function readScale(img) {
  let raw = '';
  try {
    if (typeof img === 'string') raw = img;
    else if (img && img.style) raw = String(img.style.transform || '');
  } catch (e) { return 1; }
  const m = /scale\(\s*([^(),]+?)\s*\)/.exec(raw);
  if (!m) return 1;
  const v = Number(m[1]);
  return (Number.isFinite(v) && v > 0) ? v : 1;
}

// 현재 → 목표 배율을 MM 휠 규칙으로 쪼갠 deltaY 배열(확대 = 음수).
// 예: (1, 1.6) → [-100, -100, -40] · (2, 1.5) → [100, 100] · (1, 1.003) → [](잔여는 버린다).
export function wheelStepsFor(current, target) {
  const c = Number(current);
  const t = Number(target);
  if (!Number.isFinite(c) || !Number.isFinite(t)) return [];
  const out = [];
  let d = t - c;
  for (let i = 0; i < MAX_WHEEL_STEPS; i += 1) {
    if (Math.abs(d) < STEP_EPS) break;
    const step = (d > 0 ? 1 : -1) * Math.min(Math.abs(d), SCALE_DELTA);
    out.push(round6(-(step / SCALE_DELTA) * WHEEL_UNIT));
    d -= step;
  }
  return out;
}

// 한 손가락 가로 밀기 판정 — 왼쪽으로 밀기(dx<0) = 다음 그림.
export function swipeDir(dx, dy, ms) {
  const x = Number(dx);
  const y = Number(dy);
  const t = Number(ms);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(t)) return null;
  if (Math.abs(x) < SWIPE_MIN_PX) return null;
  if (Math.abs(x) < SWIPE_RATIO * Math.abs(y)) return null;
  if (t > SWIPE_MAX_MS) return null;
  return x < 0 ? 'next' : 'prev';
}

// 그림 칸에서만 브라우저 기본 제스처를 끈다(스크롤·더블탭 확대가 우리 처리와 겹치지 않게).
export function ensureTouchCss(doc) {
  if (!doc || !doc.head) return null;
  try {
    const found = doc.getElementById(STYLE_ID);
    if (found) { if (found.textContent !== TOUCH_CSS) found.textContent = TOUCH_CSS; return found; }
    const st = doc.createElement('style');
    st.id = STYLE_ID;
    st.textContent = TOUCH_CSS;
    doc.head.appendChild(st);
    return st;
  } catch (e) { return null; }
}

function viewOf(doc) {
  try { return (doc && doc.defaultView) || (typeof window !== 'undefined' ? window : null); } catch (e) { return null; }
}

// 🔴 손가락 정보는 e.touches/e.changedTouches 로만 읽는다 — `instanceof TouchEvent` 로 가리면
//    엔진에 TouchEvent 가 없는 환경(데스크톱 Safari·jsdom)에서 통째로 걸러진다.
function points(e, key) {
  const out = [];
  if (!e) return out;
  let list = null;
  try { list = e[key]; } catch (err) { return out; }
  if (!list) return out;
  const n = (typeof list.length === 'number') ? list.length : 0;
  for (let i = 0; i < n; i += 1) {
    const t = list[i];
    if (!t) continue;
    const x = Number(t.clientX);
    const y = Number(t.clientY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    out.push({ clientX: x, clientY: y });
  }
  return out;
}

function dist(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.sqrt((dx * dx) + (dy * dy));
}

function prevent(e) {
  try { if (e && e.cancelable !== false && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) { /* */ }
}

// 🔴 합성 이벤트는 doc.defaultView(iframe 쪽) 생성자로 만든다 — 최상위 창 생성자로 만들면 iframe 안
//    instanceof 검사·React 합성이벤트 경로에서 어긋날 수 있다.
// WheelEvent 가 없는 엔진에서는 MouseEvent('wheel') 에 deltaY·deltaMode 를 직접 심는다.
// bubbles 는 쓰지 않는다 — MM 의 wheel 리스너는 래퍼에 네이티브로 직접 걸려 있다.
function fireWheel(doc, target, deltaY, clientX, clientY) {
  if (!target) return false;
  const w = viewOf(doc);
  const WE = (w && w.WheelEvent) || (typeof WheelEvent !== 'undefined' ? WheelEvent : null);
  if (WE) {
    try {
      target.dispatchEvent(new WE('wheel', {
        deltaY, deltaMode: 0, clientX, clientY, cancelable: true,
      }));
      return true;
    } catch (e) { /* MouseEvent 폴백으로 내려간다 */ }
  }
  const ME = (w && w.MouseEvent) || (typeof MouseEvent !== 'undefined' ? MouseEvent : null);
  if (!ME) return false;
  try {
    const ev = new ME('wheel', { clientX, clientY, cancelable: true });
    Object.defineProperty(ev, 'deltaY', { configurable: true, get: () => deltaY });
    Object.defineProperty(ev, 'deltaMode', { configurable: true, get: () => 0 });
    target.dispatchEvent(ev);
    return true;
  } catch (e) { return false; }
}

// mousedown 은 래퍼에(MM 은 React onMouseDown = 위임 리스너라 bubbles 필수),
// mousemove·mouseup 은 MM 이 걸어 둔 그 문서의 document 에 보낸다.
function fireMouse(doc, target, type, init) {
  if (!target) return false;
  const w = viewOf(doc);
  const ME = (w && w.MouseEvent) || (typeof MouseEvent !== 'undefined' ? MouseEvent : null);
  if (!ME) return false;
  try { target.dispatchEvent(new ME(type, init)); return true; } catch (e) { return false; }
}

// MM handleKeyPress 는 document 의 keyup 을 본다. 판정(`M.e`)은 `key` 로 통과하지만, keyCode 로만
// 보는 옛 경로를 위해 keyCode·which 도 함께 심는다(못 심으면 key·code 만으로 보낸다).
function fireKey(doc, key, keyCode) {
  const w = viewOf(doc);
  const KE = (w && w.KeyboardEvent) || (typeof KeyboardEvent !== 'undefined' ? KeyboardEvent : null);
  if (!KE || !doc) return false;
  try {
    const ev = new KE('keyup', {
      key, code: key, keyCode, which: keyCode, bubbles: true, cancelable: true,
    });
    if (ev.keyCode !== keyCode) {
      try {
        Object.defineProperty(ev, 'keyCode', { configurable: true, get: () => keyCode });
        Object.defineProperty(ev, 'which', { configurable: true, get: () => keyCode });
      } catch (e) { /* key·code 만으로 보낸다 */ }
    }
    doc.dispatchEvent(ev);
    return true;
  } catch (e) { return false; }
}

// doc 당 1회 설치. 반환 = dispose(리스너·주입 CSS 정리 + 끌기 중이면 mouseup 1회). 이미 설치면 null.
export function setupMmPreviewTouch(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwMmPreviewTouch) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  doc.__gwMmPreviewTouch = true;
  ensureTouchCss(doc);

  let disposed = false;
  // 제스처 하나 동안의 상태. mode: null | 'pinch' | 'drag' | 'swipe' | 'done'
  let mode = null;
  let multi = false;        // 이 제스처에서 손가락이 2개가 된 적이 있나(있으면 밀기 취소)
  let areaEl = null;
  let wrap = null;
  let img = null;
  let d0 = 0;               // 핀치 시작 거리
  let s0 = 1;               // 핀치 시작 배율(DOM 에서 읽는 유일한 시점)
  let cur = 1;              // 내부 누적 배율 — 보낸 wheel 만큼 우리가 직접 더한다
  let x0 = 0; let y0 = 0; let t0 = 0;
  let lastX = 0; let lastY = 0;

  function resetGesture() {
    mode = null; multi = false; areaEl = null; wrap = null; img = null;
    d0 = 0; s0 = 1; cur = 1; x0 = 0; y0 = 0; t0 = 0; lastX = 0; lastY = 0;
  }

  // 제스처의 첫 touchstart 에서 한 번만 — 미리보기 그림이 아니면 false(아무것도 보내지 않는다).
  function acquire(e) {
    let area = null; let w = null; let im = null;
    try {
      const t = e && e.target;
      area = (t && typeof t.closest === 'function') ? t.closest(AREA_SEL) : null;
      w = area ? area.querySelector(WRAP_SEL) : null;
      im = w ? w.querySelector(IMG_SEL) : null;
    } catch (err) { return false; }
    if (!area || !w || !im) return false;
    areaEl = area; wrap = w; img = im;
    return true;
  }

  function endDrag(p) {
    fireMouse(doc, doc, 'mouseup', {
      clientX: p ? p.clientX : lastX, clientY: p ? p.clientY : lastY,
      button: 0, buttons: 0, bubbles: true, cancelable: true,
    });
  }

  function resetButton() {
    try {
      const root = (areaEl && typeof areaEl.closest === 'function' && areaEl.closest(MODAL_SEL)) || doc;
      const bar = root.querySelector(ZOOM_BAR_SEL);
      const icon = bar ? bar.querySelector(RESET_ICON_SEL) : null;
      return (icon && typeof icon.closest === 'function') ? icon.closest('a') : null;
    } catch (e) { return null; }
  }

  // 핀치를 100% 근처에서 떼면 MM 의 «원래 크기» 버튼을 눌러 정확히 1·이동 0 으로 맞춘다.
  // 🔴 그래야 다음 한 손가락이 «끌기»가 아니라 «밀기»로 읽힌다(MM 은 기본 배율 초과에서만 끈다).
  //    MM 휠 경로는 새 배율이 기본값과 «정확히» 같을 때만 이동을 0 으로 되돌리므로 1.0 에 못 닿는다.
  function snapIfNear() {
    if (cur === 1 || Math.abs(cur - 1) >= SNAP_EPS) return false;
    const btn = resetButton();
    if (!btn) return false;
    if (!fireMouse(doc, btn, 'click', { button: 0, buttons: 0, bubbles: true, cancelable: true })) return false;
    cur = 1;
    return true;
  }

  const onStart = (e) => {
    if (disposed || !isActive()) return;
    const pts = points(e, 'touches');
    if (!pts.length) return;
    if (mode === null && !acquire(e)) return;   // 미리보기 그림이 아니다 — 손대지 않는다
    if (!wrap || !img) return;
    if (pts.length >= 2) {
      multi = true;
      if (mode === 'drag') endDrag(pts[0]);
      mode = 'pinch';
      d0 = dist(pts[0], pts[1]);
      s0 = readScale(img);
      cur = s0;
      prevent(e);
      return;
    }
    if (mode !== null) return;   // 핀치가 끝난 뒤 남은 손가락으로는 새 제스처를 시작하지 않는다
    const p = pts[0];
    x0 = p.clientX; y0 = p.clientY; t0 = Date.now(); lastX = x0; lastY = y0;
    if (readScale(img) > DRAG_SCALE_EPS) {
      mode = 'drag';
      fireMouse(doc, wrap, 'mousedown', {
        button: 0, buttons: 1, clientX: x0, clientY: y0, bubbles: true, cancelable: true,
      });
      prevent(e);
      return;
    }
    mode = 'swipe';   // 🔴 여기서는 preventDefault 하지 않는다 — 짧은 탭이 클릭으로 가야 한다
  };

  const onMove = (e) => {
    if (disposed || !isActive()) return;
    if (mode === null || mode === 'done') return;
    const pts = points(e, 'touches');
    if (mode === 'pinch') {
      if (pts.length < 2 || !(d0 > 0) || !wrap) return;
      const target = clampScale(s0 * (dist(pts[0], pts[1]) / d0));
      const steps = wheelStepsFor(cur, target);
      const cx = (pts[0].clientX + pts[1].clientX) / 2;
      const cy = (pts[0].clientY + pts[1].clientY) / 2;
      for (let i = 0; i < steps.length; i += 1) {
        if (!fireWheel(doc, wrap, steps[i], cx, cy)) break;
        cur = clampScale(cur + ((-steps[i] / WHEEL_UNIT) * SCALE_DELTA));
      }
      prevent(e);
      return;
    }
    if (!pts.length) return;
    const p = pts[0];
    lastX = p.clientX; lastY = p.clientY;
    if (mode === 'drag') {
      fireMouse(doc, doc, 'mousemove', {
        clientX: p.clientX, clientY: p.clientY, button: 0, buttons: 1, bubbles: true, cancelable: true,
      });
      prevent(e);
      return;
    }
    // 밀기 — 가로로 충분히 움직였으면 탭 클릭·브라우저 기본 동작이 끼지 않게 막는다.
    if (mode === 'swipe' && Math.abs(p.clientX - x0) > SWIPE_PREVENT_PX) prevent(e);
  };

  const onEnd = (e) => {
    if (disposed) return;
    const left = points(e, 'touches');
    if (!isActive()) { if (!left.length) resetGesture(); return; }
    const changed = points(e, 'changedTouches');
    const end = changed.length ? changed[0] : { clientX: lastX, clientY: lastY };
    if (mode === 'drag') {
      endDrag(end);
      mode = 'done';
    } else if (mode === 'pinch') {
      snapIfNear();
      mode = 'done';          // 남은 손가락으로 그림이 튀지 않게 이 제스처는 여기서 닫는다
    } else if (mode === 'swipe') {
      // touchcancel 은 밀기로 보지 않는다. 제스처 중 손가락이 2개가 된 적이 있으면(= 핀치였다) 취소.
      if (e && e.type === 'touchend' && !multi) {
        const dir = swipeDir(end.clientX - x0, end.clientY - y0, Date.now() - t0);
        if (dir === 'next') fireKey(doc, 'ArrowRight', 39);
        else if (dir === 'prev') fireKey(doc, 'ArrowLeft', 37);
      }
      mode = 'done';
    }
    if (!left.length) resetGesture();
  };

  try {
    // 🔴 capture:true — MM(과 MobileScale)보다 먼저 본다. passive:false — preventDefault 가 먹어야 한다.
    doc.addEventListener('touchstart', onStart, { capture: true, passive: false });
    doc.addEventListener('touchmove', onMove, { capture: true, passive: false });
    doc.addEventListener('touchend', onEnd, { capture: true, passive: false });
    doc.addEventListener('touchcancel', onEnd, { capture: true, passive: false });
  } catch (e) { /* */ }

  return function dispose() {
    if (disposed) return;
    disposed = true;
    if (mode === 'drag') { try { endDrag(null); } catch (e) { /* */ } }
    try {
      doc.removeEventListener('touchstart', onStart, true);
      doc.removeEventListener('touchmove', onMove, true);
      doc.removeEventListener('touchend', onEnd, true);
      doc.removeEventListener('touchcancel', onEnd, true);
    } catch (e) { /* */ }
    try { const st = doc.getElementById(STYLE_ID); if (st && st.parentNode) st.parentNode.removeChild(st); } catch (e) { /* */ }
    resetGesture();
    try { doc.__gwMmPreviewTouch = false; } catch (e) { /* */ }
  };
}
